import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs';
import { APP_CONFIG, LoaderService } from '../core';
import { MainCanvasLoaderService } from '../services';
import { BaseComponent, ConfirmationComponent } from '../shared';
import { CanvasTabsetComponent } from './canvas-tabset';
import { LayersComponent } from './layers';
import { MessagesComponent } from './messages';


/**
* Canvas view component.
*/
@Component({
	selector: 'canvas3d',
	templateUrl: './canvas3d.component.html',
	imports: [
		DecimalPipe,
		LayersComponent,
		MessagesComponent,
		CanvasTabsetComponent
	],
	changeDetection: ChangeDetectionStrategy.Eager,
})
export class Canvas3DComponent extends BaseComponent implements OnInit {

	private readonly config = inject(APP_CONFIG);
	private readonly modalSrv = inject(NgbModal);
	private readonly loaderSrv = inject(LoaderService);
	private readonly mainCanvLoadSrv = inject(MainCanvasLoaderService);


	ngOnInit(): void {
		this.initCanvas();
	}

	private initCanvas(): void {
		this.loaderSrv.show();
		this.mainCanvLoadSrv.init().pipe(
			finalize(() => this.loaderSrv.hide())
		).subscribe({ error: (err: Error) => this.handlePlanError(err) });
	}

	/**
	* Check if is Ap view active in multiple state.
	*/
	get isMultipleAP(): boolean {
		return this.activeViewState === this.activeViewStateEnum.multiple && this.activeView === this.activeViewEnum.AP;
	}

	/**
	* Fit to view the current active view.
	*/
	fitToView(): void {
		this.canvasSrv.dispatch('fitToView');
	}

	/**
	* Zoom in the current active view.
	*/
	zoomIn(): void {
		this.canvasSrv.dispatch('zoomIn');
	}

	/**
	* Zoom out the current active view.
	*/
	zoomOut(): void {
		this.canvasSrv.dispatch('zoomOut');
	}

	private handlePlanError(err: Error): void {
		switch (err.message) {
			case 'PlanNotFound': {
				this.openInvalidPlanModal(this.labels().PLAN_DELETED_WARNING_MESSAGE);
				break;
			}
			case 'PlanNotValid': {
				this.openInvalidPlanModal(this.labels().INVALID_PLAN_MODAL_MESSAGE);
				break;
			}
			case 'WebglDisabled': {
				this.openInvalidPlanModal(this.labels().WEBGL_INVALID_WARNING_MESSAGE);
				break;
			}
			case 'ScreenResolutionInvalid': {
				this.openInvalidPlanModal(this.labels().RESOLUTION_INVALID_WARNING_MESSAGE);
				break;
			}
			default:
				throw err;
		}
	}

	private openInvalidPlanModal(message: string): void {
		const modalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
		(modalRef.componentInstance as ConfirmationComponent).config = {
			title: this.labels().INVALID_PLAN_MODAL_TITLE,
			message,
			confirmBtn: this.labels().INVALID_PLAN_MODAL_CONFIRM_BTN,
			isCancelBtn: false
		};
		modalRef.result.then(() => globalThis.location.href = this.config.patplanSite);
	}
}
